import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import { SectionIntro } from "../components/ContentBlocks";
import { seoConfig } from "../data/seoConfig";

const topicPillars = [
  {
    title: "Robotics companies and manufacturers",
    description: "A serious company guide separates robot makers from software platforms, component suppliers and integrators. The useful comparison starts with robot type, deployment proof, safety case, service model and customer environment.",
    links: [
      { label: "Robotics companies", to: "/robotics-companies" },
      { label: "Robot types", to: "/robot-types" },
      { label: "Humanoid robots", to: "/robot-types/humanoid-robots" }
    ]
  },
  {
    title: "Robot software and fleet operations",
    description: "Robot software coverage should explain middleware, navigation, planning, simulation, teleoperation, monitoring, maintenance, APIs and the operator dashboard that keeps a fleet useful after the demo ends.",
    links: [
      { label: "Robotics software", to: "/robotics-software" },
      { label: "Robot management software", to: "/robot-management-software" },
      { label: "Robotics data collection", to: "/robotics-data-collection" }
    ]
  },
  {
    title: "Robot hardware and components",
    description: "Hardware articles need visible engineering detail motors, reducers, bearings, encoders, batteries, controllers, cameras, LiDAR, tactile sensors, grippers, payload limits and maintenance cost.",
    links: [
      { label: "Robot components", to: "/robot-components" },
      { label: "Robot sensors", to: "/robot-sensors" },
      { label: "Robotic arm joints", to: "/robotic-arm-joint-components" }
    ]
  },
  {
    title: "Events, field robots and deployment evidence",
    description: "Events and field robotics coverage must identify the task, autonomy boundary, environment, intervention rate, sensor setup, repeatability and next deployment step.",
    links: [
      { label: "Robotics events", to: "/robotics-events" },
      { label: "Nuclear robots", to: "/nuclear-robots" },
      { label: "Robotics news", to: "/robotics-news" }
    ]
  }
];

function PillarCard({ pillar }) {
  return (
    <article className="soft-card p-5 sm:p-6">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950">{pillar.title}</h2>
      <p className="mt-4 text-sm leading-7 text-neutral-600">{pillar.description}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        {pillar.links.map((link) => (
          <Link key={link.to} to={link.to} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-black text-neutral-900 transition hover:border-blue-200 hover:text-blue-700">
            {link.label}
          </Link>
        ))}
      </div>
    </article>
  );
}

export default function SearchConsoleTopics() {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robotics Editorial Topic Map", to: "/robotics-search-topics" }
  ];

  const schema = [
    buildBreadcrumbSchema(breadcrumbs),
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      headline: "Robotics Editorial Topic Map",
      description: "A TechniaHQRobot map for robotics companies, software, components, events, data collection and field robot explainers.",
      author: { "@type": "Organization", name: "TechniaHQRobot" },
      publisher: { "@type": "Organization", name: "TechniaHQRobot" },
      mainEntityOfPage: `${seoConfig.baseUrl}/robotics-search-topics`
    }
  ];

  return (
    <>
      <SEO
        title="Robotics Editorial Topic Map"
        description="Navigate TechniaHQRobot robotics guides by company type, software layer, hardware stack, events, field robots and deployment evidence."
        path="/robotics-search-topics"
        type="article"
        schema={schema}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Robotics topic map"
        title="Find robotics guides by topic"
        description="Explore companies, software, components and field robots using the guides below."
      />

      <section className="site-shell py-10 sm:py-14">
        <ArticleAd placement="top" articleSlug="robotics-search-topics" className="mb-8" />
        <SectionIntro
          eyebrow="Explore the guides"
          title="Choose the right guide before comparing robots"
          description="A humanoid company, an AMR vendor, a robot arm maker and a simulation platform answer different buyer questions. Each section maps the technical problem to the right TechniaHQRobot guide."
        />
        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          {topicPillars.map((pillar) => <PillarCard key={pillar.title} pillar={pillar} />)}
        </div>
      </section>

      <section className="site-shell pb-10 sm:pb-14">
        <ArticleAd placement="bottom" articleSlug="robotics-search-topics" />
      </section>

      <CTASection title="Follow TechniaHQRobot for robotics articles built around real machines and real deployment limits" />
    </>
  );
}
